import {
    type FC,
    memo
} from "react";
import {
    type AccessibilityActionEvent,
    type NativeSyntheticEvent,
    type TargetedEvent,
    Platform,
    View
} from "react-native";
import type ITreeViewItemProps from "./type";
import type {
    TreeViewItemWebPropsType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useTreeViewSelector,
    useTreeViewContext
} from "../../context";
import {
    TREE_VIEW_POINTER_FOCUS_OPTIONS,
    TREE_VIEW_ASSISTIVE_ACTIONS,
    getIsTreeViewPartTarget,
    focusTreeViewElement
} from "../../util";
import CollapseRegion from "../../../collapsible/internal/collapseRegion";
import TreeViewGroup from "../group";
import TreeViewRow from "../row";

const TreeViewItemContent: FC<ITreeViewItemProps> = ({
    entry
}) => {
    const {
        handleLongPressEntry,
        handleToggleExpand,
        customLocalize,
        handleFocusKey,
        renderItemMenu,
        selectionMode,
        customTheme,
        itemRefs
    } = useTreeViewContext();

    const isSelected = useTreeViewSelector((snapshot) => snapshot.selectedKeys.has(entry.key));

    const checkedState = useTreeViewSelector((snapshot) => snapshot.checkedStates.get(entry.key));

    const isTabStop = useTreeViewSelector((snapshot) => snapshot.tabStopKey === entry.key);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState();

    const isError = entry.kind === "error";

    const isExpandable = entry.isBranch && !entry.isDrillTarget && !isError;

    const menu = !isError && !entry.isDisabled ? renderItemMenu?.(entry.node) : null;

    const getLabel = () => {
        if(isError) {
            return localize("tree-view-load-failed");
        }

        if(entry.isDrillTarget) {
            return localize("tree-view-drill-label", [
                entry.node.title
            ]);
        }

        return entry.node.title;
    };

    const getSelectedProps = () => {
        if(isError) {
            return {};
        }

        if(selectionMode === "single") {
            return {
                "aria-selected": isSelected
            };
        }

        if(selectionMode === "multiple") {
            return {
                "aria-checked": checkedState === "indeterminate" ? "mixed" as const : checkedState === "checked"
            };
        }

        return {};
    };

    const getAccessibilityActions = () => {
        const actions = [];

        if(isExpandable && !entry.isDisabled) {
            actions.push(entry.isExpanded ? {
                name: TREE_VIEW_ASSISTIVE_ACTIONS.collapse,
                label: localize("tree-view-collapse")
            } : {
                name: TREE_VIEW_ASSISTIVE_ACTIONS.expand,
                label: localize("tree-view-expand")
            });
        }

        if(menu) {
            actions.push({
                name: TREE_VIEW_ASSISTIVE_ACTIONS.longpress,
                label: localize("more-actions")
            });
        }

        return actions;
    };

    const handleRef = (node: View | null) => {
        if(node) {
            itemRefs.current.set(entry.key, node);

            return;
        }

        itemRefs.current.delete(entry.key);
    };

    const getIsOwnEvent = (event: NativeSyntheticEvent<TargetedEvent>) => {
        return Platform.OS !== "web" || event.target === event.currentTarget;
    };

    const getIsPartEvent = (event: NativeSyntheticEvent<TargetedEvent>) => {
        return getIsTreeViewPartTarget({
            node: event.currentTarget,
            target: event.target
        });
    };

    const onFocus = (event: NativeSyntheticEvent<TargetedEvent>) => {
        if(getIsPartEvent(event)) {
            focusTreeViewElement(event.currentTarget, TREE_VIEW_POINTER_FOCUS_OPTIONS);

            return;
        }

        if(!getIsOwnEvent(event)) {
            return;
        }

        handleFocusKey(entry.key);

        interactionHandlers.onFocus(event);
    };

    const onBlur = (event: NativeSyntheticEvent<TargetedEvent>) => {
        if(!getIsOwnEvent(event)) {
            return;
        }

        interactionHandlers.onBlur(event);
    };

    const onAccessibilityAction = (event: AccessibilityActionEvent) => {
        switch(event.nativeEvent.actionName) {
            case TREE_VIEW_ASSISTIVE_ACTIONS.collapse:
            case TREE_VIEW_ASSISTIVE_ACTIONS.expand:
                handleToggleExpand(entry);

                break;
            case TREE_VIEW_ASSISTIVE_ACTIONS.longpress:
                handleLongPressEntry(entry);

                break;
            default:
                break;
        }
    };

    const webProps: TreeViewItemWebPropsType = Platform.OS === "web" ? {
        dataSet: interactionHandlers.dataSet
    } : {};

    const renderItem = (child: ITreeViewItemProps["entry"]) => {
        return <TreeViewItem
            key={child.key}
            entry={child}
        />;
    };

    const renderGroup = () => {
        if(!isExpandable) {
            return null;
        }

        return <CollapseRegion
            isOpen={entry.isExpanded}
            customTheme={customTheme}
        >
            <TreeViewGroup
                renderItem={renderItem}
                entry={entry}
            />
        </CollapseRegion>;
    };

    return <View
        {...getSelectedProps()}
        {...webProps}
        aria-expanded={isExpandable ? entry.isExpanded : undefined}
        accessibilityActions={getAccessibilityActions()}
        onAccessibilityAction={onAccessibilityAction}
        aria-disabled={entry.isDisabled || undefined}
        aria-busy={entry.isLoading || undefined}
        aria-posinset={entry.posInSet}
        tabIndex={isTabStop ? 0 : -1}
        aria-setsize={entry.setSize}
        style={stylesheet.container}
        aria-level={entry.level}
        aria-label={getLabel()}
        collapsable={false}
        onFocus={onFocus}
        role="treeitem"
        onBlur={onBlur}
        ref={handleRef}
    >
        <TreeViewRow
            isFocusVisible={isFocusVisible}
            entry={entry}
            menu={menu}
        />
        {renderGroup()}
    </View>;
};
const TreeViewItem = memo(TreeViewItemContent);
export default TreeViewItem;
